import React, { useCallback, useContext, useEffect, useState } from 'react';
import { View, Pressable } from 'react-native';
import { useQuery, useMutation } from '@apollo/client';
import { useForm, FormProvider } from 'react-hook-form';
import DataTable from '@preflighttech/preflight-tables';

import { AuthContext } from 'app/src/contexts/AuthContext';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { Fields, defaultValues } from 'app/src/elements/hookForms';
import minimalTable from 'app/src/elements/minimalTable';
import NestedFields from 'app/src/elements/NestedFields';
import MutationErrors from 'app/src/elements/MutationErrors';
import DataState from 'app/src/components/DataState';
import { Text } from 'app/src/styles';

import { PAYMENT_SELECTS } from './queries';

import {
  Section,
  FIELDS,
  LINE_FIELDS,
  EMPTY_LINE,
  invoiceColumns,
} from './new';
import { UPDATE_TXN } from '../Txns/queries';
import TxnActions from '../Txns/TxnActions';

import {
  getBalance,
  prepareTxnInput,
  creditLessDebitTxnLine,
} from '../Txns/utils';

const EditPayment = ({ txn, setShowModal, refetch }) => {
  const { accounts, role } = useContext(AuthContext);
  const [showAllInvoiceLines, setShowAllInvoiceLines] = useState(false);

  const invoiceLines = [];
  const regularLines = [];

  txn.txnLines.forEach(txnLine => {
    if (txnLine.offsetId && 'ar' === txnLine.account.special) {
      invoiceLines.push(txnLine);
    } else {
      regularLines.push(txnLine);
    }
  });

  if (1 === regularLines.length) {
    regularLines.push([EMPTY_LINE]);
  }

  const [invoiceAmounts, setInvoiceAmounts] = useState(
    invoiceLines.map(invoiceLine => ({
      id: invoiceLine.offsetId,
      amount: -invoiceLine.debitLessCredit,
    }))
  );

  const [nestedResources, setNestedResources] = useState(
    regularLines
      .filter((txnLine, index) => index > 0)
      .map(creditLessDebitTxnLine)
  );

  const [formStatus, setFormStatus] = useState();
  const [mutationErrors, setMutationErrors] = useState();

  const { data: selects } = useQuery(
    PAYMENT_SELECTS, {
      variables: { ids: invoiceLines.map(invoiceLine => invoiceLine.offsetId) },
      fetchPolicy: 'no-cache',
    }
  );

  const {
    register, control, handleSubmit, setValue, watch, formState: { errors }
  } = useForm({
    defaultValues: defaultValues({
      fields: FIELDS,
      resource: { ...txn, balanceAccountId: txn.txnLines[0].account.id },
      nestedFields: [{
        nestedResources,
        prefix: 'txnLinesAttributes',
        fields: LINE_FIELDS,
      }],
    })
  });

  const [updateTxn] = useMutation(UPDATE_TXN, {
    onCompleted: data => {
      if (data.updateTxn.errors?.length) {
        setFormStatus(null);
        setMutationErrors(data.updateTxn.errors);
      } else {
        refetch();
        setShowModal(false);
      }
    }
  });

  const onSubmit = useCallback(input => {
    setFormStatus('submitting');
    input.type = 'Payment';
    updateTxn({
      variables: prepareTxnInput(
        input, FIELDS, {
          invoiceAmounts,
          accounts,
          originalLines: txn.txnLines,
        }
      )
    });
  });

  const watchAll = watch();

  useEffect(() => {
    const txnLinesWatch = watchAll?.txnLinesAttributes;

    if (txnLinesWatch) {
      const lastLine = txnLinesWatch[txnLinesWatch.length - 1];

      if (lastLine.creditLessDebit) {
        const prefix = `txnLinesAttributes.${nestedResources.length}`;

        register(`${prefix}.accountId`, { value: '' });
        register(`${prefix}.creditLessDebit`, { value: '' });
        register(`${prefix}.memo`, { value: '' });
        setNestedResources([...nestedResources, EMPTY_LINE]);
      }
    }
  }, [watchAll]);

  if (!selects) { return <DataState.Loading /> };

  const fields = { ...FIELDS };
  const lineFields = { ...LINE_FIELDS };

  const availableLines = (txn.entityId && !showAllInvoiceLines) ?
    selects.invoiceLines.filter(buyerLine => (
      buyerLine.txn.entityType === txn.entityType &&
      buyerLine.txn.entityId === txn.entityId &&
      !selects.existingInvoiceLines.map(line => line.id).includes(buyerLine.id)
    )) : [...selects.invoiceLines];

  return (
    <View>
      {'Developer' === role && (
        <TxnActions txn={txn} refetch={refetch} />
      )}

      <FormProvider watch={watch}>
        <Fields
          fields={FIELDS}
          control={control}
          errors={errors}
          setValue={setValue}
        />

        <Section>
          <View style={{ flexDirection: 'row', gap: 20 }}>
            <Text>Open Invoices</Text>
            <Pressable
              onPress={() => setShowAllInvoiceLines(!showAllInvoiceLines)}
            >
              <Text>[Show open invoices for all buyers]</Text>
            </Pressable>
          </View>

          <DataTable.Simple
            data={[...selects.existingInvoiceLines, ...availableLines]}
            columns={invoiceColumns({ invoiceAmounts, setInvoiceAmounts })}
            component={minimalTable}
            pageLength="All"
          />
        </Section>

        <Section>
          <NestedFields
            prefix="txnLinesAttributes"
            resources={nestedResources}
            fields={lineFields}
            register={register}
            control={control}
            errors={errors}
          />
        </Section>

        <MutationErrors errors={mutationErrors} />

        <ButtonGroup>
          <Button label="Update Payment" onPress={handleSubmit(onSubmit)} />
        </ButtonGroup>
      </FormProvider>
    </View>
  );
};

export default EditPayment;
